import { IReactiveModel, ViewModel } from '@descomplica/microfrontend-core';
import { IsEmail, IsNotEmpty } from 'class-validator';
import { action, makeAutoObservable, observable } from 'mobx';
import { NavigateFunction } from 'react-router-dom';

import { UrlUtils } from '@/shared/application/utils/url';

import { SignInDto } from '../../../../../shared/application/interfaces/pegasus-io.gateway';
import { ObservableForm } from '../../../../../shared/presentation/forms/BaseForm';
import { MicrofrontendViewModel } from '../../../../../shared/presentation/Microfrontend.vm';
import { SignInCollaboration } from '../../../application/collaborations/signin.collaboration';

class SignInForm {
  @IsEmail({}, { message: 'E-mail inválido' })
  email = '';

  @IsNotEmpty({ message: 'Campo obrigatório' })
  password = '';
}

class ForgotPasswordForm {
  @IsEmail({}, { message: 'E-mail inválido' })
  @IsNotEmpty({ message: 'Campo obrigatório' })
  email = '';
}

@ViewModel()
export class SignInViewModel implements IReactiveModel {
  @observable form = new ObservableForm(SignInForm);
  @observable forgotPasswordForm = new ObservableForm(ForgotPasswordForm);
  @observable isSubmitting = false;
  @observable errorMessage = '';
  @observable withHelpCenterLink = false;
  @observable isForgotPasswordModalOpen = false;
  @observable isSubmittingForgotPassword = false;
  @observable enabledSignUp = false;

  private executeRecaptcha: (action: string) => Promise<string>;
  private navigate: NavigateFunction;

  constructor(
    private signIncollaboration: SignInCollaboration,
    private microfrontendViewModel: MicrofrontendViewModel,
  ) {
    makeAutoObservable(this);
  }

  @action
  async onInit({ navigate }) {
    try {
      this.navigate = navigate;
      this.enabledSignUp = UrlUtils.getQueryParam('token') !== null;

      if (this.microfrontendViewModel.account) {
        setTimeout(() => {
          navigate('/');
        }, 100);
        return;
      }
    } catch (error) {
      console.error(error);
      this.errorMessage = 'Ocorreu um erro ao tentar carregar a página.';
    }
  }

  @action
  onChanges({ executeRecaptcha }) {
    this.executeRecaptcha = executeRecaptcha;
  }

  @action
  setIsForgotPasswordModalOpen(isOpen: boolean) {
    this.isForgotPasswordModalOpen = isOpen;
  }

  @action
  sendEmailForgotPassword(email: string) {
    this.isSubmittingForgotPassword = true;
  }

  async signIn() {
    try {
      if (this.form.hasError) {
        return;
      }

      this.isSubmitting = true;
      this.errorMessage = '';
      this.withHelpCenterLink = false;

      if (!this.executeRecaptcha) {
        throw new Error('Execute recaptcha not yet available');
      }

      const googleRecaptchaResponse = await this.executeRecaptcha('SignIn');

      const invitationToken = UrlUtils.getQueryParam('token');

      const dto: SignInDto = {
        email: this.form.values.email,
        password: this.form.values.password,
        googleRecaptchaResponse,
        token: invitationToken,
      };

      await this.signIncollaboration.run(dto);
      await this.microfrontendViewModel.identifyCurrentAccount();

      const redirectUrlParam = new URLSearchParams(window.location.search).get('redirectUrl');

      if (redirectUrlParam) {
        try {
          const allowedRedirectDomains = ['descomplica.com.br', 'descomplica.io', 'localhost'];
          const url = decodeURIComponent(redirectUrlParam);
          const urlBaseDomain = new URL(url).hostname.replace(/^[^.]+\./g, '');

          if (allowedRedirectDomains.includes(urlBaseDomain)) {
            window.location.href = url;
            return;
          }
        } catch (error) {
          this.navigate('/');
          return;
        }
      }

      this.navigate('/');
    } catch (error) {
      if (error.response?.data?.identifier === 'InvalidRecaptcha') {
        this.errorMessage =
          'Desculpe, não conseguimos verificar a integridade do seu acesso de forma automática. Por favor, tente novamente mais tarde.';
        this.withHelpCenterLink = true;
        return;
      }

      if (error?.response?.status === 400) {
        this.errorMessage = 'Email e/ou senha informados são inválidos';
        return;
      }

      this.errorMessage = 'Ocorreu um erro ao tentar fazer login.';
    } finally {
      this.isSubmitting = false;
    }
  }

  goToSignUp(): void {
    const token = UrlUtils.getQueryParam('token');
    this.navigate(`/signup${token ? `?token=${token}` : ''}`);
  }
}
